import React, { useMemo } from 'react';
import {
  format, addDays, subDays, addWeeks, subWeeks,
  startOfWeek, endOfWeek, isSameDay, isToday,
} from 'date-fns';
import { ChevronLeft, ChevronRight, Plus, UserCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Apt {
  id: string;
  patient_id: string;
  date: string;
  time: string;
  reason: string;
  status: string;
  doctor_id?: string;
  patient_name?: string;
  patient_age?: number;
  patient_gender?: string;
}

interface Props {
  view: 'day' | 'week';
  date: Date;
  appointments: Apt[];
  isFrontDesk: boolean;
  checkingInId?: string | null;
  onNavigate: (d: Date) => void;
  onNewAppointment: (date: string, time?: string) => void;
  onEditAppointment: (apt: Apt) => void;
  onCheckIn: (apt: Apt) => void;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const START_HOUR = 8;
const END_HOUR   = 20;
const HOUR_PX    = 64; // px per hour in day view
const HOURS      = Array.from({ length: END_HOUR - START_HOUR }, (_, i) => START_HOUR + i);

const STATUS_STYLE: Record<string, { border: string; bg: string; text: string; dot: string }> = {
  scheduled:  { border: 'border-primary/40',     bg: 'bg-primary/10',     text: 'text-primary',     dot: 'bg-primary'        },
  completed:  { border: 'border-success/40',     bg: 'bg-success/10',     text: 'text-success',     dot: 'bg-success'        },
  cancelled:  { border: 'border-destructive/40', bg: 'bg-destructive/10', text: 'text-destructive', dot: 'bg-destructive/60' },
  checked_in: { border: 'border-warning/40',     bg: 'bg-warning/10',     text: 'text-warning',     dot: 'bg-warning'        },
  no_show:    { border: 'border-destructive/40', bg: 'bg-destructive/10', text: 'text-destructive', dot: 'bg-destructive/60' },
  in_progress:{ border: 'border-primary/60',     bg: 'bg-primary/5',      text: 'text-primary',     dot: 'bg-primary'        },
};

function styleFor(status: string) {
  return STATUS_STYLE[status] ?? STATUS_STYLE.scheduled;
}

function fmtTime(t: string) {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  const d = new Date(); d.setHours(h, m);
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
}

function parseTime(t: string) {
  const [h, m] = (t || '00:00').split(':').map(Number);
  return { hour: h || 0, minutes: m || 0 };
}

function aptTopPx(time: string) {
  const { hour, minutes } = parseTime(time);
  const clamped = Math.max(START_HOUR, Math.min(END_HOUR - 1, hour));
  return (clamped - START_HOUR + minutes / 60) * HOUR_PX;
}

// ─── Nav header shared by both views ─────────────────────────────────────────

function NavHeader({
  label, onPrev, onNext, onToday,
}: { label: string; onPrev: () => void; onNext: () => void; onToday: () => void }) {
  return (
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-2">
        <button onClick={onPrev} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors">
          <ChevronLeft className="w-4 h-4" />
        </button>
        <span className="text-sm font-semibold text-foreground min-w-[200px] text-center">{label}</span>
        <button onClick={onNext} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors">
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
      <button
        onClick={onToday}
        className="text-xs font-medium px-3 py-1.5 rounded-lg border border-border hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
      >
        Today
      </button>
    </div>
  );
}

// ─── Appointment pill (shared) ────────────────────────────────────────────────

function AptPill({
  apt, compact, isFrontDesk, checkingInId,
  onClick, onCheckIn,
}: {
  apt: Apt; compact?: boolean; isFrontDesk: boolean;
  checkingInId?: string | null;
  onClick: () => void; onCheckIn: (e: React.MouseEvent) => void;
}) {
  const s = styleFor(apt.status);
  return (
    <div
      onClick={onClick}
      className={`rounded-lg border-l-2 px-2 py-1 cursor-pointer hover:brightness-95 transition-all overflow-hidden ${s.border} ${s.bg}`}
    >
      <p className={`text-xs font-semibold truncate ${s.text}`}>
        {apt.patient_name || 'Patient'}
      </p>
      <p className="text-[10px] text-muted-foreground truncate">
        {fmtTime(apt.time)}{!compact && apt.reason ? ` · ${apt.reason}` : ''}
      </p>
      {!compact && apt.status === 'scheduled' && isFrontDesk && (
        <button
          onClick={onCheckIn}
          disabled={checkingInId === apt.id}
          className={`mt-1 flex items-center gap-0.5 text-[10px] font-semibold text-success hover:text-success/80 ${checkingInId === apt.id ? 'opacity-50' : ''}`}
        >
          <UserCheck className="w-2.5 h-2.5" />
          {checkingInId === apt.id ? '...' : 'Check In'}
        </button>
      )}
    </div>
  );
}

// ─── Day view ─────────────────────────────────────────────────────────────────

function DayView({ date, appointments, isFrontDesk, checkingInId, onNavigate, onNewAppointment, onEditAppointment, onCheckIn }: Omit<Props, 'view'>) {
  const dateStr = format(date, 'yyyy-MM-dd');

  const dayApts = useMemo(() =>
    appointments.filter(a => a.date.split('T')[0] === dateStr),
  [appointments, dateStr]);

  return (
    <div>
      <NavHeader
        label={format(date, 'EEEE, d MMMM yyyy')}
        onPrev={() => onNavigate(subDays(date, 1))}
        onNext={() => onNavigate(addDays(date, 1))}
        onToday={() => onNavigate(new Date())}
      />

      <div className="border border-border rounded-xl overflow-hidden bg-card">
        {/* Scrollable grid */}
        <div className="overflow-y-auto" style={{ maxHeight: '65vh' }}>
          <div className="relative" style={{ height: `${HOURS.length * HOUR_PX}px` }}>

            {/* Hour lines + labels */}
            {HOURS.map(h => (
              <div
                key={h}
                className="absolute left-0 right-0 border-t border-border/40 flex"
                style={{ top: `${(h - START_HOUR) * HOUR_PX}px`, height: `${HOUR_PX}px` }}
              >
                <div className="w-14 flex-shrink-0 pr-2 pt-1 text-right">
                  <span className="text-[10px] text-muted-foreground">
                    {h % 12 || 12}{h < 12 ? 'am' : 'pm'}
                  </span>
                </div>
                {/* Click to add */}
                <div
                  className="flex-1 hover:bg-primary/5 cursor-pointer transition-colors group"
                  onClick={() => onNewAppointment(dateStr, `${String(h).padStart(2, '0')}:00`)}
                >
                  <Plus className="w-3.5 h-3.5 text-primary opacity-0 group-hover:opacity-100 mt-1 ml-1 transition-opacity" />
                </div>
              </div>
            ))}

            {/* Appointment blocks */}
            {dayApts.map(apt => {
              const top = aptTopPx(apt.time);
              return (
                <div
                  key={apt.id}
                  className="absolute left-16 right-3"
                  style={{ top: `${top}px`, minHeight: `${HOUR_PX * 0.6}px`, zIndex: 1 }}
                >
                  <AptPill
                    apt={apt}
                    isFrontDesk={isFrontDesk}
                    checkingInId={checkingInId}
                    onClick={() => onEditAppointment(apt)}
                    onCheckIn={e => { e.stopPropagation(); onCheckIn(apt); }}
                  />
                </div>
              );
            })}

            {dayApts.length === 0 && (
              <div
                className="absolute inset-0 left-14 flex items-center justify-center text-sm text-muted-foreground pointer-events-none"
              >
                No appointments — click a time slot to add one
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Week view ────────────────────────────────────────────────────────────────

function WeekView({ date, appointments, isFrontDesk, checkingInId, onNavigate, onNewAppointment, onEditAppointment, onCheckIn }: Omit<Props, 'view'>) {
  const weekStart = startOfWeek(date, { weekStartsOn: 1 });
  const weekDays  = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  const byDay = useMemo(() => {
    const map: Record<string, Apt[]> = {};
    weekDays.forEach(d => { map[format(d, 'yyyy-MM-dd')] = []; });
    appointments.forEach(a => {
      const key = a.date.split('T')[0];
      if (map[key]) map[key].push(a);
    });
    Object.values(map).forEach(arr => arr.sort((a, b) => a.time.localeCompare(b.time)));
    return map;
  }, [appointments, weekStart]);

  const label = `${format(weekStart, 'd MMM')} – ${format(addDays(weekStart, 6), 'd MMM yyyy')}`;

  return (
    <div>
      <NavHeader
        label={label}
        onPrev={() => onNavigate(subWeeks(date, 1))}
        onNext={() => onNavigate(addWeeks(date, 1))}
        onToday={() => onNavigate(new Date())}
      />

      <div className="border border-border rounded-xl overflow-hidden bg-card">
        {/* Day headers */}
        <div className="grid grid-cols-7 border-b border-border">
          {weekDays.map(d => {
            const today = isToday(d);
            return (
              <div
                key={d.toISOString()}
                className={`px-2 py-2.5 text-center border-r border-border/50 last:border-0 cursor-pointer hover:bg-muted/40 transition-colors ${today ? 'bg-primary/5' : ''}`}
                onClick={() => onNavigate(d)}
              >
                <p className="text-[10px] text-muted-foreground uppercase tracking-wide">
                  {format(d, 'EEE')}
                </p>
                <p className={`text-sm font-bold mt-0.5 w-7 h-7 rounded-full flex items-center justify-center mx-auto ${
                  today ? 'bg-primary text-primary-foreground' : 'text-foreground'
                }`}>
                  {format(d, 'd')}
                </p>
              </div>
            );
          })}
        </div>

        {/* Day columns */}
        <div className="grid grid-cols-7 min-h-[400px]" style={{ maxHeight: '62vh', overflowY: 'auto' }}>
          {weekDays.map(d => {
            const key = format(d, 'yyyy-MM-dd');
            const dayApts = byDay[key] ?? [];
            const today = isToday(d);
            return (
              <div
                key={key}
                className={`border-r border-border/50 last:border-0 p-1.5 min-h-[200px] ${today ? 'bg-primary/5' : ''}`}
              >
                <div className="space-y-1">
                  {dayApts.map(apt => (
                    <AptPill
                      key={apt.id}
                      apt={apt}
                      compact
                      isFrontDesk={isFrontDesk}
                      checkingInId={checkingInId}
                      onClick={() => onEditAppointment(apt)}
                      onCheckIn={e => { e.stopPropagation(); onCheckIn(apt); }}
                    />
                  ))}
                </div>
                {/* Add slot */}
                <button
                  onClick={() => onNewAppointment(key)}
                  className="mt-1 w-full text-center p-1 rounded text-[10px] text-muted-foreground/50 hover:text-primary hover:bg-primary/5 transition-colors opacity-0 hover:opacity-100"
                >
                  + Add
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── Main export ──────────────────────────────────────────────────────────────

export default function AppointmentCalendar(props: Props) {
  if (props.view === 'day') return <DayView {...props} />;
  return <WeekView {...props} />;
}
